import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { displayMoney, Money, CURRENCY } from '../../../../utils/money';
import { timestampedMessage } from '../../../common/multi-package-scanner/multi-package-scanner';
import SnackBar from '../../../common/snack-bar/snack-bar';
import { CircularProgress, Typography, Grid, Box } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { Decimal } from 'decimal.js';
import Grid2 from '@mui/material/Unstable_Grid2';

export interface GenericPendingCash<T extends object> {
  total_price: T;
  total_delivery_fee: T;
  total_order_commission: T;
  total_pickup_commission: T;
  total_pop: T;
}

type PendingCash = GenericPendingCash<Money[]>;
type SingleCurrencyPendingCash = GenericPendingCash<Decimal>;

const newEmptySingleCurrencyPendingCash = (): SingleCurrencyPendingCash => ({
  total_price: new Decimal(0),
  total_delivery_fee: new Decimal(0),
  total_order_commission: new Decimal(0),
  total_pickup_commission: new Decimal(0),
  total_pop: new Decimal(0),
});

interface PendingCashOperationProps {
  driver_id: string;
}

const PIE_CHART_COLORS = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2'];

function PendingCashOperation({ driver_id }: PendingCashOperationProps) {
  const { user, config } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCash, setTotalCash] = useState<PendingCash | null>(null);
  const [snackBar, setSnackBar] = useState<timestampedMessage | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get<PendingCash>(
          `${API_ENDPOINT}/api/v1/cash/?driver=${driver_id}`,
          config
        );
        setTotalCash(response.data);
      } catch (e) {
        setSnackBar({
          content: 'Failed to get cash',
          level: 'error',
          timestamp: Date.now(),
        });
      }
      setIsLoading(false);
    };
    fetchData();
  }, [driver_id]);

  const hasCashData = (): boolean => {
    if (!totalCash) return false;
    return Object.values(totalCash).some((moneyArray) => moneyArray.length > 0);
  };

  const getGroupedDataByCurrency = (): Partial<
    Record<CURRENCY, SingleCurrencyPendingCash>
  > => {
    const result = {
      [CURRENCY.USD]: newEmptySingleCurrencyPendingCash(),
      [CURRENCY.LBP]: newEmptySingleCurrencyPendingCash(),
      [CURRENCY.EUR]: newEmptySingleCurrencyPendingCash(),
    };

    if (!totalCash || !hasCashData()) return result;

    for (const [label, amount] of Object.entries(totalCash) as [
      string,
      Money[],
    ][]) {
      for (const [currency, value] of amount) {
        const key = label as keyof SingleCurrencyPendingCash;
        result[currency][key] = (result[currency][key] ?? new Decimal(0)).add(
          value
        );
      }
    }

    return result;
  };

  const getPieChartData = (
    currency: CURRENCY,
    data: SingleCurrencyPendingCash
  ) => {
    const totalCod = data.total_price.add(data.total_delivery_fee);
    const totalValue = totalCod
      .add(data.total_order_commission)
      .add(data.total_pickup_commission)
      .add(data.total_pop);

    if (totalValue.equals(0)) return null;

    return [
      {
        id: 'cod',
        label: 'Total COD',
        value: totalCod.toNumber(),
        color: PIE_CHART_COLORS[0],
      },
      {
        id: 'order-commission',
        label: 'Order Commission',
        value: data.total_order_commission.toNumber(),
        color: PIE_CHART_COLORS[1],
      },
      {
        id: 'pickup-commission',
        label: 'Pickup Commission',
        value: data.total_pickup_commission.toNumber(),
        color: PIE_CHART_COLORS[2],
      },
      {
        id: 'pop',
        label: 'Pay on Pickup',
        value: data.total_pop.toNumber(),
        color: PIE_CHART_COLORS[3],
      },
    ];
  };

  const renderPieChartOrEmpty = (
    currency: CURRENCY,
    data: SingleCurrencyPendingCash
  ) => {
    const pieData = getPieChartData(currency, data);

    if (!pieData) {
      return (
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #ccc',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No active cash in {currency}
          </Typography>
        </Box>
      );
    }

    // total amount = (price + delivery fee) - pop - order_commission - pickup_commission
    const totalAmount = data.total_price
      .add(data.total_delivery_fee)
      .minus(data.total_pop)
      .minus(data.total_order_commission)
      .minus(data.total_pickup_commission);

    return (
      <Box sx={{ height: '100%', width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
          {displayMoney([[currency, totalAmount]])}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', mb: 2 }}>
          Total {currency}
        </Typography>
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        >
          <PieChart
            series={[
              {
                data: pieData,
                innerRadius: 60,
                outerRadius: 100,
                paddingAngle: 4,
                cornerRadius: 4,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: {
                  innerRadius: 30,
                  additionalRadius: -30,
                  color: 'gray',
                },
              },
            ]}
            margin={{ right: 250 }}
            slotProps={{
              legend: {
                direction: 'column',
                position: {
                  horizontal: 'right',
                  vertical: 'middle',
                },
              },
            }}
            width={400}
            height={200}
          />
        </div>
      </Box>
    );
  };

  return (
    <div style={{ width: '100%', textAlign: 'center' }}>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 1, mb: 1 }}
      >
        Driver Active Cash
      </Typography>

      {isLoading ? (
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress size={24} />
          <Typography sx={{ ml: 1 }}>Loading cash details...</Typography>
        </Grid>
      ) : !hasCashData() ? (
        <Box
          sx={{
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No active cash available
          </Typography>
        </Box>
      ) : (
        <Grid2 container spacing={2}>
          {Object.entries(getGroupedDataByCurrency()).map(
            ([currency, data]) => (
              <Grid2 xs={12} md={4} key={currency}>
                <Typography variant="h6" gutterBottom textAlign="center">
                  Breakdown for {currency}
                </Typography>

                {renderPieChartOrEmpty(currency as CURRENCY, data)}
              </Grid2>
            )
          )}
        </Grid2>
      )}

      {snackBar && snackBar.content && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}
    </div>
  );
}

export default PendingCashOperation;
